import React from 'react';
import { cn } from '@utils/cn';
import {
  computePerLineDiscount,
  DiscountInput,
  DiscountScope,
  DiscountType,
} from '@utils/discount';

interface DiscountToggleProps {
  /** Current ($/%, value) pair the toggle is editing. */
  value: DiscountInput;
  onChange: (next: DiscountInput) => void;
  /** Unit price used to convert PERCENT into the per-unit dollar discount preview. */
  unitPrice: number;
  /** Line quantity used to compute the total discount preview. */
  quantity: number;
  /**
   * `per-unit` (default) — `$` value is multiplied by quantity to produce the line discount.
   * `per-line` — `$` value is the total dollar discount applied directly to the line.
   * `%` behaves identically under either scope (just changes the displayed label).
   */
  scope?: DiscountScope;
  /** Optional override label above the toggle. */
  label?: string;
  /** Optional hint shown under the label. */
  helper?: string;
  /** Optional inline error message. */
  error?: string;
  className?: string;
}

const formatCurrency = (amount: number, currency = 'USD'): string => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  } catch {
    return `$${amount.toFixed(2)}`;
  }
};

/**
 * Reusable $/% discount input. Consumes a `DiscountInput` and emits the new
 * `DiscountInput` on every change so callers can hold the state wherever they
 * want (Formik field, parent React state, etc.). Renders a live "Line discount"
 * and "Net charged after discount" preview underneath, computed via the shared
 * `computePerLineDiscount` utility.
 */
export const DiscountToggle: React.FC<DiscountToggleProps> = ({
  value,
  onChange,
  unitPrice,
  quantity,
  scope = 'per-unit',
  label,
  helper,
  error,
  className,
}) => {
  const effectiveLabel = label ?? (scope === 'per-line' ? 'Line discount' : 'Discount per unit');
  const { discountAmount, netTotal, grossTotal } = computePerLineDiscount(
    unitPrice,
    quantity,
    value.type,
    value.value,
    scope,
  );
  const active = !!value.type && Number(value.value || 0) > 0 && discountAmount > 0;

  const setType = (next: DiscountType) => {
    onChange({ type: next, value: value.value });
  };

  const setValue = (raw: string) => {
    const numeric = raw === '' ? 0 : Number(raw);
    // Auto-pick USD when the user types a positive value before choosing a type,
    // so the live preview updates from the very first keystroke.
    const nextType: DiscountType | null =
      value.type ?? (numeric > 0 ? 'USD' : null);
    onChange({ type: nextType, value: numeric });
  };

  const clear = () => onChange({ type: null, value: 0 });

  return (
    <div className={cn('w-full', className)}>
      {effectiveLabel && (
        <label className="block text-sm font-medium text-gray-700 mb-1">{effectiveLabel}</label>
      )}
      {helper && <p className="text-xs text-gray-500 mb-2">{helper}</p>}
      <div className="flex items-center gap-2">
        <div className="inline-flex rounded-md border border-gray-300 overflow-hidden flex-shrink-0">
          <button
            type="button"
            onClick={() => setType('USD')}
            className={cn(
              'px-3 py-2 text-sm font-medium min-w-[44px]',
              value.type === 'USD'
                ? 'bg-blue-900 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50',
            )}>
            $
          </button>
          <button
            type="button"
            onClick={() => setType('PERCENT')}
            className={cn(
              'px-3 py-2 text-sm font-medium border-l border-gray-300 min-w-[44px]',
              value.type === 'PERCENT'
                ? 'bg-blue-900 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50',
            )}>
            %
          </button>
        </div>
        <div className="relative flex-1">
          {value.type === 'USD' && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
              $
            </span>
          )}
          <input
            type="number"
            min={0}
            step="0.01"
            placeholder="0"
            value={value.value === 0 ? '' : value.value}
            onChange={(e) => setValue(e.target.value)}
            className={cn(
              'w-full py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500',
              value.type === 'USD' && 'pl-7 pr-3',
              value.type === 'PERCENT' && 'pl-3 pr-7',
              !value.type && 'px-3',
            )}
          />
          {value.type === 'PERCENT' && (
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-500">
              %
            </span>
          )}
        </div>
      </div>
      {value.type !== null && (
        <button
          type="button"
          onClick={clear}
          className="text-xs text-gray-500 hover:text-gray-700 hover:underline mt-1.5">
          Clear discount
        </button>
      )}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
      {active && (
        <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2 flex items-center justify-between">
          <span className="text-xs text-amber-800 uppercase tracking-wide">
            Line discount
            {value.type === 'PERCENT' ? ` (${value.value}%)` : ''}
          </span>
          <span className="text-sm font-semibold text-amber-900">
            −{formatCurrency(discountAmount)}
          </span>
        </div>
      )}
      {active && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mt-2 flex items-center justify-between">
          <span className="text-xs text-emerald-700 uppercase tracking-wide">
            Net charged after discount
          </span>
          <span className="text-base font-semibold text-emerald-900">
            {formatCurrency(netTotal)}
          </span>
        </div>
      )}
      {!active && grossTotal > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 mt-2 flex items-center justify-between">
          <span className="text-xs text-gray-500 uppercase tracking-wide">Line total</span>
          <span className="text-sm font-semibold text-gray-700">
            {formatCurrency(grossTotal)}
          </span>
        </div>
      )}
    </div>
  );
};
